import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { useEffect, lazy, Suspense } from "react";
import { Text, AppShell, Group, Loader, Center } from "@mantine/core";
import { OfflineIndicator } from "@/components/OfflineIndicator";

// Check if Clerk is available at build time
const CLERK_PUBLISHABLE_KEY = import.meta.env["VITE_CLERK_PUBLISHABLE_KEY"] as string | undefined;

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const location = useLocation();

  // Public routes that don't need the app shell or auth
  const isPublicRoute = location.pathname.startsWith("/submit");

  if (isPublicRoute) {
    return (
      <>
        <Outlet />
        {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      </>
    );
  }

  return (
    <>
      {CLERK_PUBLISHABLE_KEY ? <ClerkAuthWrapper /> : <NoAuthWrapper />}
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
}

// Wrapper when Clerk is NOT available - show app directly for demo/testing
function NoAuthWrapper() {
  const navigate = useNavigate();
  const location = useLocation();

  // In demo mode, redirect from home to projects
  useEffect(() => {
    if (location.pathname === "/") {
      navigate({ to: "/projects" });
    }
  }, [location.pathname, navigate]);

  return (
    <>
      <OfflineIndicator />
      <AppShell header={{ height: 60 }} padding="md">
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between">
            <Text size="lg" fw={700}>
              Kablan
            </Text>
            <Text size="sm" c="dimmed">
              Demo Mode
            </Text>
          </Group>
        </AppShell.Header>

        <AppShell.Main>
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </>
  );
}

// Lazy load the Clerk auth wrapper only when Clerk is available
const ClerkAuthInner = lazy(() => import("@/components/ClerkAuthWrapper"));

function ClerkAuthWrapper() {
  return (
    <Suspense
      fallback={
        <Center style={{ minHeight: "100vh" }}>
          <Loader size="lg" />
        </Center>
      }
    >
      <ClerkAuthInner />
    </Suspense>
  );
}
